import { useTheme } from '@/app/providers';
import { cn } from '@/shared/cn';
import { useBrand } from './BrandContext';

// =========================================================================
// BrandMark — the one place the product wordmark is drawn. Fork-owned.
//
// Replaces every hard-coded "Foundation" literal so a brand switch moves the
// sidebar header, the auth hero, the signup/lobby heroes and the review-rail
// home button together. Three variants:
//
//   lockup    "AiRis ProjeX" + OEM+ badge + the OEM's logo. A real client
//             (Komatsu) reads "for <logo>"; the fictional Meridian sits beside
//             the wordmark with a hairline and no "for", so the default demo
//             never claims a client (`Brand.lockupFor`). The demo's top-bar
//             treatment; used by OemTopBar and the auth heroes.
//   wordmark  "AiRis ProjeX" + OEM+ badge. No client logo — for narrow chrome
//             (the sidebar header) where the client logo would truncate.
//   mark      A 16px accent glyph. For icon slots (the review rail's home
//             button) where only a square fits.
//
// The client logo is an <img>, not an inline SVG: the asset stays a file with
// a recorded hash in public/brands/README.md, and swapping a brand is a src
// change rather than a bundle change.
// =========================================================================

export type BrandMarkVariant = 'lockup' | 'wordmark' | 'mark';

export function BrandMark({
  variant = 'wordmark',
  className,
  logoHeight = 18,
}: {
  variant?: BrandMarkVariant;
  className?: string;
  /** Rendered height of the client logo in the lockup, in px. */
  logoHeight?: number;
}) {
  const { brand } = useBrand();
  const { theme } = useTheme();
  const logoSrc = theme === 'dark' ? brand.logoReversed : brand.logo;

  if (variant === 'mark') {
    return (
      <span
        className={cn('inline-flex items-center justify-center', className)}
        aria-hidden="true"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" role="presentation">
          <circle
            cx="10"
            cy="13"
            r="6.5"
            fill="none"
            stroke="var(--accent)"
            strokeWidth="2.2"
          />
          <path
            d="M18 5v6M15 8h6"
            fill="none"
            stroke="var(--brand-secondary)"
            strokeWidth="2.2"
            strokeLinecap="round"
          />
        </svg>
      </span>
    );
  }

  const wordmark = (
    <span className="inline-flex items-baseline gap-1.5 whitespace-nowrap">
      <span className="font-semibold tracking-tight text-zinc-900 dark:text-zinc-100">
        AiRis ProjeX
      </span>
      <span
        className="rounded px-1 py-px text-[10px] font-semibold uppercase leading-[1.4] tracking-[0.06em]"
        style={{ background: 'var(--accent-soft)', color: 'var(--accent)' }}
      >
        OEM+
      </span>
    </span>
  );

  if (variant === 'wordmark') {
    return (
      <span className={cn('inline-flex min-w-0 items-center', className)}>
        {wordmark}
      </span>
    );
  }

  return (
    <span
      className={cn('inline-flex min-w-0 items-center gap-2', className)}
      aria-label={
        brand.lockupFor
          ? `AiRis ProjeX OEM+ for ${brand.client}`
          : `AiRis ProjeX OEM+ · ${brand.client}`
      }
    >
      {wordmark}
      {brand.lockupFor ? (
        <span className="text-xs text-muted" aria-hidden="true">
          for
        </span>
      ) : (
        <span
          aria-hidden="true"
          className="block w-px flex-shrink-0"
          style={{ background: 'var(--hairline-strong)', minHeight: logoHeight }}
        />
      )}
      <img
        src={logoSrc}
        alt={brand.client}
        title={brand.client}
        height={logoHeight}
        style={{ height: logoHeight, width: 'auto' }}
        className="block flex-shrink-0"
      />
    </span>
  );
}
